import React, { Component, ReactNode } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { ErrorOutline, Build, Storage } from '@mui/icons-material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string;
  retryCount: number;
}

class DatabaseErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorId: '',
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): State {
    const errorId = Date.now().toString();
    return { 
      hasError: true, 
      error,
      errorId,
      retryCount: 0
    };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('Database Error Boundary caught an error:', error, errorInfo);
    
    // Prevent infinite error loops by checking error patterns
    if (this.isInfiniteLoopError(error)) {
      console.warn('Potential infinite loop detected, preventing further retries');
      return;
    }
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  isInfiniteLoopError = (error: Error): boolean => {
    const infiniteLoopPatterns = [
      'Maximum update depth exceeded',
      'Too many re-renders',
      'Cannot update a component while rendering'
    ];
    
    return infiniteLoopPatterns.some(pattern => 
      error.message?.includes(pattern)
    );
  };

  resetErrorBoundary = () => {
    if (this.state.retryCount >= this.maxRetries) {
      console.warn('Maximum retry attempts reached');
      return;
    }

    // Clear any existing timeout
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    // Add a small delay to prevent immediate re-rendering issues
    this.retryTimeout = setTimeout(() => {
      this.setState(prevState => ({ 
        hasError: false, 
        error: null,
        errorId: '',
        retryCount: prevState.retryCount + 1
      }));
    }, 100);
  };

  handleRefresh = () => {
    // Clear any timeouts before refresh
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || '';
      
      const isDatabaseError = errorMessage.includes('relation') || 
                             errorMessage.includes('does not exist') ||
                             errorMessage.includes('42P01');
      
      const isHookError = errorMessage.includes('Invalid hook call') ||
                         errorMessage.includes('Maximum update depth') ||
                         errorMessage.includes('Too many re-renders');

      const isInfiniteLoop = this.isInfiniteLoopError(this.state.error!);
      const hasReachedMaxRetries = this.state.retryCount >= this.maxRetries;

      if (isDatabaseError || isHookError) {
        return (
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              p: 3,
            }}
          >
            <Paper
              elevation={3}
              sx={{
                maxWidth: 600,
                p: 4,
                textAlign: 'center',
              }}
            >
              <Storage sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              
              <Typography variant="h4" gutterBottom color="error">
                {isDatabaseError ? 'Database Setup Required' : 'Application Error'}
              </Typography>
              
              {this.state.retryCount > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Retry attempt: {this.state.retryCount}/{this.maxRetries}
                </Typography>
              )}
              
              <Alert severity={isDatabaseError ? 'warning' : 'error'} sx={{ mb: 3, textAlign: 'left' }}>
                <AlertTitle>
                  {isDatabaseError ? 'Database Tables Not Found' : 
                   isHookError ? 'React Component Error' : 'Application Error'}
                </AlertTitle>
                {isDatabaseError ? 
                  'The application cannot connect to the database tables. This is a one-time setup issue.' :
                  isHookError ? 
                    'A React component error occurred. This may be due to improper hook usage or component lifecycle issues.' :
                    'An unexpected error occurred in the application.'}
                {isInfiniteLoop && (
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                    ⚠️ Infinite loop detected - automatic retries disabled
                  </Typography>
                )}
              </Alert>

              <Box sx={{ textAlign: 'left', mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <Build sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Quick Fix Steps:
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="1. Go to Supabase Dashboard" 
                      secondary="Visit https://supabase.com/dashboard and select your project"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="2. Open SQL Editor" 
                      secondary="Navigate to SQL Editor in the left sidebar"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="3. Run Schema Creation" 
                      secondary="Copy contents of schema.sql and execute it"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="4. Seed Sample Data" 
                      secondary="Copy contents of seed_data.sql and execute it"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="5. Create Test Users" 
                      secondary="Add authentication users in the Auth section"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                For detailed instructions, check the SETUP_INSTRUCTIONS.md file
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={this.resetErrorBoundary}
                  disabled={hasReachedMaxRetries || isInfiniteLoop}
                >
                  {hasReachedMaxRetries ? 'Max Retries Reached' : 'Try Again'}
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={this.handleRefresh}
                >
                  Refresh Page
                </Button>
              </Box>
              
              {(hasReachedMaxRetries || isInfiniteLoop) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  If the problem persists, please check the browser console for detailed error information.
                </Typography>
              )}
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem', wordBreak: 'break-word' }}>
                    {this.state.error.stack}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        );
      }

      // Fallback for any other errors not specifically handled
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            p: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              maxWidth: 500,
              p: 4,
              textAlign: 'center',
            }}
          >
            <ErrorOutline sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            
            <Typography variant="h5" gutterBottom color="error">
              Something went wrong
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              An unexpected error occurred. Please try refreshing the page.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                onClick={this.handleRefresh}
              >
                Refresh Page
              </Button>
            </Box>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'left' }}>
                <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem', wordBreak: 'break-word' }}>
                  {this.state.error.stack}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default DatabaseErrorBoundary;